import { isSurfTemplate } from '@/utils/api';

const DEFAULT_THEME_MODE = '#3964e5';
export const getDefaultThemeColor = () => {
	const isSurfTmpl = isSurfTemplate();
	if (isSurfTmpl) {
		return '#000000';
	} else {
		return DEFAULT_THEME_MODE;
	}
};

const COLOR_PALETTE = {
	'#3964e5': {
		'name': 'blue',
		'primary': '#3964e5',
		'light-svg': '#DBE2FF',
		'gradients-charts': ['#3964e5', '#73DCFF', '#9673FF', '#FF7373']
	},
	'#F23D3D': {
		'name': 'red',
		'primary': '#F23D3D',
		'light-svg': '#FFDBDB',
		'gradients-charts': ['#F23D3D', '#F26A3D', '#FFDC73', '#C6E567']
	},
	'#ad053d': {
		'name': 'nightshade',
		'primary': '#ad053d',
		'light-svg': '#FFDBDB',
		'gradients-charts': ['#ad053d', '#F26A3D', '#FFDC73', '#C6E567']
	},
	'#f26a3d': {
		'name': 'orange',
		'primary': '#f26a3d',
		'light-svg': '#FFD9C2',
		'gradients-charts': ['#f26a3d', '#FFB973', '#FFDC73', '#C6E567']
	},
	'#8539e6': {
		'name': 'purple',
		'primary': '#8539e6',
		'light-svg': '#EEDBFF',
		'gradients-charts': ['#8539e6', '#FF7373', '#FFB973', '#E5E567']
	},
	'#30bf54': {
		'name': 'green',
		'primary': '#30bf54',
		'light-svg': '#EBFBEF',
		'gradients-charts': ['#30bf54', '#C6E567', '#FFB973', '#FF7373']
	},
	'#1D2129': {
		'name': 'black',
		'primary': '#1D2129',
		'light-svg': '#DBE2FF',
		'gradients-charts': ['#1D2129', '#73DCFF', '#9673FF', '#FF7373']
	},
	'#D2A36C': {
		'name': 'brown',
		'primary': '#D2A36C',
		'light-svg': '#DBE2FF',
		'gradients-charts': ['#D2A36C', '#73DCFF', '#9673FF', '#FF7373']
	},
	'#1E9A4C': {
		'name': 'light-green',
		'primary': '#1E9A4C',
		'light-svg': '#EBFBEF',
		'gradients-charts': ['#1E9A4C', '#C6E567', '#FFB973', '#FF7373']
	},

	/*
		Add any color:
		color: {
			color-name,
			color,
			svg-color-light,
			charts-colors,
		}
	*/

	// 翻页卡片
	'#000000': {
		'name': 'dark',
		'primary': '#000000',
		'light-svg': '#DBE2FF',
		'gradients-charts': ['#000000', '#73DCFF', '#9673FF', '#FF7373']
	},
	'#FFFFFF': {
		'name': 'light',
		'primary': '#3964e5',
		'light-svg': '#DBE2FF',
		'gradients-charts': ['#3964e5', '#73DCFF', '#9673FF', '#FF7373']
	}
};

function generateColorMap(callback) {
	const map = {};
	Object.keys(COLOR_PALETTE).forEach(key => {
		const colorData = COLOR_PALETTE[key];
		let result = callback(colorData, key);

		// 如果是处理 gradients-charts，需要补全透明度版本
		if (callback.toString().includes('gradients-charts')) {
			const originalColors = colorData['gradients-charts'];
			const extendedColors = [...originalColors];

			// 先为所有颜色生成80%透明度版本
			originalColors.forEach(color => {
				extendedColors.push(hexToRgba(color, 0.8));
			});

			// 再为所有颜色生成60%透明度版本
			originalColors.forEach(color => {
				extendedColors.push(hexToRgba(color, 0.6));
			});

			result = extendedColors;
		}

		map[key.toUpperCase()] = result;
	});

	return map;
}

// 辅助函数：将十六进制颜色转换为带透明度的rgba格式
function hexToRgba(hex, alpha) {
	const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
	if (result) {
		const r = parseInt(result[1], 16);
		const g = parseInt(result[2], 16);
		const b = parseInt(result[3], 16);
		return `rgba(${r}, ${g}, ${b}, ${alpha})`;
	}
	return hex; // 如果解析失败，返回原色值
}

const colorArray = Object.keys(COLOR_PALETTE);
// 别名映射
const colorAliases = generateColorMap(item => item.name);
// 浅色映射
const lightColors = generateColorMap(item => item['light-svg']);
// 渐变色映射
const gradientColors = generateColorMap(item => item['gradients-charts']);

export const themeColorCtx = {
	colorArray,
	colorAliases,
	lightColors,
	gradientColors
};

const transparencyValues = [0, 1, 2, 3, 4, 5, 10, 20];
function hexToRgb(hex) {
	const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
	return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '0, 0, 0';
}
function bindThemeAttribute(node, theme) {
	node.setAttribute('theme-mode', `theme-${theme}`);
	node.style.setProperty('--theme-color', theme);
	transparencyValues.forEach(value => {
		const formattedValue = value < 10 ? `00${value}` : value < 100 ? `0${value}` : `${value}`;
		const gradient = `rgba(${hexToRgb(theme)}, ${value / 100})`;
		node.style.setProperty(`--theme-color-gradient-${formattedValue}`, gradient);
	});
}

const storeTheme = {
	namespaced: true,
	state: () => ({
		predefineColors: colorArray
	}),
	mutations: {
		bindAppThemeStyle(state, data) {
			let safeColor;
			const defaultColor = getDefaultThemeColor();
			const upperColor = (data || defaultColor).toLocaleUpperCase();
			// 兜底，检查传入的色值是否为记录好的主题规则
			const includeTheme = state.predefineColors.map(c => c.toLocaleUpperCase()).includes(upperColor);
			if (includeTheme) {
				safeColor = upperColor;
			} else {
				safeColor = defaultColor.toLocaleUpperCase();
			}
			const appNode = document.querySelector('body');
			bindThemeAttribute(appNode, safeColor);
		}
	}
};

export default storeTheme;
